import React, { MouseEvent, useState } from 'react';
import { useField } from 'react-final-form';

import { FWorkflowTriggerFragment } from '@dashboard/generated/wp/platform';
import { ExpandMoreOutlined } from '@mui/icons-material';
import { Button, Menu, Stack, TextField, Tooltip } from '@mui/material';

import useTriggersMutator from '../../useTriggersMutator';

type BaseProps = {
  value: string;
  onSubmit: (key: string | null) => void;
  onRemove?: () => void;
};
function BaseWorkflowKeyDropdown({ value, onSubmit, onRemove }: BaseProps) {
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const [key, setKey] = useState<null | string>(value);
  const open = Boolean(anchorEl);
  const handleOpen = (ev: MouseEvent<HTMLButtonElement>) => {
    setAnchorEl(ev.currentTarget);
  };
  const handleClose = () => {
    setAnchorEl(null);
  };

  const handleRemove = () => {
    setKey('');
    if (onRemove) {
      onRemove();
    }
  };

  const onSubmitClick = () => {
    onSubmit(key);
    setAnchorEl(null);
  };

  return (
    <>
      <Tooltip title="Use a more descriptive workflow key name instead of a workflow ID">
        <Button
          onClick={handleOpen}
          size="small"
          endIcon={<ExpandMoreOutlined fontSize="small" />}
          color="secondary"
          sx={{ fontSize: 12 }}
        >
          Custom workflow key
        </Button>
      </Tooltip>
      <Menu anchorEl={anchorEl} open={open} onClose={handleClose}>
        <Stack direction="row" alignItems="flex-start" spacing={0.5} sx={{ p: 1.5 }}>
          <TextField onChange={(ev) => setKey(ev.target.value)} size="small" value={key} autoFocus />
          <Button onClick={onSubmitClick}>Done</Button>
        </Stack>

        <Button disabled={!onRemove} onClick={handleRemove} sx={{ color: 'brand.red', fontSize: 12, ml: 1 }}>
          Remove key
        </Button>
      </Menu>
    </>
  );
}

type UpdateProps = {
  name: string;
};
export const UpdateWorkflowKeyDropdown = ({ name }: UpdateProps) => {
  const { fields } = useTriggersMutator();
  const {
    input: { value, onChange },
  } = useField<FWorkflowTriggerFragment>(name);
  return (
    <BaseWorkflowKeyDropdown
      value={value.workflowKey?.key ?? ''}
      onRemove={() => fields.pop()}
      onSubmit={(key: string | null) => {
        onChange({ workflowKey: { key } });
      }}
    />
  );
};

export const CreateWorkflowKeyDropdown = () => {
  const { fields } = useTriggersMutator();
  return (
    <BaseWorkflowKeyDropdown
      value=""
      onSubmit={(key: string | null) => {
        fields.push({ workflowKey: { key } });
      }}
    />
  );
};
